/**
 * What to tell a person when a Supabase call fails. One function, both apps.
 *
 * The database writes its refusals for the person who will read them (CLAUDE.md "Show
 * RPC refusals verbatim"):
 *
 *   P0001  a refusal the user can act on ("Choose a timing slot") → shown VERBATIM
 *   42501  not allowed                                             → "You don't have access to this"
 *   P0002  not found (and PostgREST 404 / PGRST116)                → "Not found"
 *
 * "Not found" is deliberately bare. RLS makes another gym's record and a record that
 * does not exist look the same; a hedged "you may not have access" would undo that.
 *
 * Errors arrive in several shapes: PostgrestError ({ code, message, details, hint }),
 * AuthError ({ status, code, message }), StorageError ({ statusCode, message }),
 * FunctionsHttpError (body unread) and plain network TypeErrors. Edge Functions in this
 * project answer `{ error: { code, message } }`; `callFunction` turns that into a
 * `GymError` so it lands here with its code intact.
 */

export type ErrorKind =
  | 'refusal'
  | 'forbidden'
  | 'not_found'
  | 'unauthenticated'
  | 'invalid'
  | 'network'
  | 'unknown';

export interface DescribedError {
  kind: ErrorKind;
  /** Safe to show as written. */
  message: string;
  code: string | null;
  status: number | null;
  /** Retrying a refusal gets the same refusal; only transient failures are retryable. */
  retryable: boolean;
  /** Field-level messages, when a function reported them. */
  fieldErrors: Record<string, string>;
}

export const MESSAGES = {
  forbidden: "You don't have access to this",
  notFound: 'Not found',
  unauthenticated: 'Your session has ended. Please sign in again.',
  network: "Can't reach Fitnatic. Check your connection and try again.",
  unknown: 'Something went wrong. Please try again.',
  duplicate: 'That already exists.',
  invalid: 'Some details are not valid. Check them and try again.',
} as const;

/** An error with a known code and a message written for people. */
export class GymError extends Error {
  readonly code: string | null;
  readonly status: number | null;
  readonly fieldErrors: Record<string, string>;

  constructor(
    message: string,
    opts: { code?: string | null; status?: number | null; fieldErrors?: Record<string, string> } = {},
  ) {
    super(message);
    this.name = 'GymError';
    this.code = opts.code ?? null;
    this.status = opts.status ?? null;
    this.fieldErrors = opts.fieldErrors ?? {};
  }
}

interface ErrorLike {
  message?: unknown;
  code?: unknown;
  status?: unknown;
  statusCode?: unknown;
  name?: unknown;
  fieldErrors?: unknown;
}

function asRecord(value: unknown): ErrorLike | null {
  return value !== null && typeof value === 'object' ? (value as ErrorLike) : null;
}

function str(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

function num(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && /^\d{3}$/.test(value)) return Number(value);
  return null;
}

function described(
  kind: ErrorKind,
  message: string,
  code: string | null,
  status: number | null,
  fieldErrors: Record<string, string> = {},
): DescribedError {
  return { kind, message, code, status, retryable: kind === 'network' || kind === 'unknown', fieldErrors };
}

/** Classify any thrown or returned error and choose the words to show. */
export function describeError(error: unknown): DescribedError {
  if (error === null || error === undefined) return described('unknown', MESSAGES.unknown, null, null);
  if (typeof error === 'string') return described('refusal', error, null, null);

  const e = asRecord(error);
  if (!e) return described('unknown', MESSAGES.unknown, null, null);

  const code = str(e.code);
  const status = num(e.status) ?? num(e.statusCode);
  const message = str(e.message);
  const fieldErrors =
    e.fieldErrors && typeof e.fieldErrors === 'object' ? (e.fieldErrors as Record<string, string>) : {};

  // Postgres error codes first: they are the most specific signal.
  switch (code) {
    case 'P0001':
      return described('refusal', message ?? MESSAGES.unknown, code, status, fieldErrors);
    case '42501':
      return described('forbidden', MESSAGES.forbidden, code, status);
    case 'P0002':
    case 'PGRST116': // .single() found no row
    case 'PGRST205': // table not in the schema cache
    case 'PGRST202': // function not in the schema cache
      return described('not_found', MESSAGES.notFound, code, status);
    case '23505':
      return described('invalid', MESSAGES.duplicate, code, status);
    case '23502':
    case '23514':
    case '22P02':
    case '22007':
    case '22008':
    case '23503':
      return described('invalid', MESSAGES.invalid, code, status);
    case 'PGRST301': // JWT expired / invalid
    case 'PGRST302':
    case 'session_expired':
    case 'session_not_found':
    case 'refresh_token_not_found':
    case 'bad_jwt':
      return described('unauthenticated', MESSAGES.unauthenticated, code, status);
    case 'invalid_credentials':
      return described('refusal', 'That sign-in did not work. Check your details and try again.', code, status);
    case 'otp_expired':
      return described('refusal', 'That code has expired or is not right. Ask for a new one.', code, status);
    case 'over_email_send_rate_limit':
    case 'over_request_rate_limit':
      return described('refusal', 'Too many attempts. Please wait a minute and try again.', code, status);
    default:
      break;
  }

  // A GymError without a Postgres code carries a message written for people.
  if (error instanceof GymError && message && status !== null && status < 500 && status !== 401 &&
      status !== 403 && status !== 404) {
    return described('refusal', message, code, status, fieldErrors);
  }

  if (status === 401) return described('unauthenticated', MESSAGES.unauthenticated, code, status);
  if (status === 403) return described('forbidden', MESSAGES.forbidden, code, status);
  if (status === 404) return described('not_found', MESSAGES.notFound, code, status);

  // fetch() failing outright: offline, DNS, CORS, the server down.
  const name = str(e.name);
  if (
    (name === 'TypeError' && message && /fetch|network|load failed/i.test(message)) ||
    name === 'AuthRetryableFetchError' ||
    name === 'FunctionsFetchError' ||
    // postgrest-js returns a plain object whose message is "TypeError: Failed to fetch".
    (message && /^(TypeError: )?(Failed to fetch|Network request failed|NetworkError|Load failed)/i.test(message))
  ) {
    return described('network', MESSAGES.network, code, status);
  }

  // Supabase Auth and Storage messages are written for people ("Invalid login
  // credentials", "The object exceeded the maximum allowed size"); show them.
  if ((name === 'AuthApiError' || name === 'StorageApiError' || name === 'StorageError') && message) {
    return described('refusal', message, code, status);
  }

  if (status !== null && status >= 400 && status < 500 && message) {
    return described('refusal', message, code, status, fieldErrors);
  }
  return described('unknown', MESSAGES.unknown, code, status);
}

/** The words to show for an error. */
export function errorMessage(error: unknown): string {
  return describeError(error).message;
}

/**
 * Throw if a Supabase response carries an error, else return its data (which may be
 * null, e.g. an RPC returning void). Use in query and mutation functions so TanStack
 * Query sees a real rejection:
 *
 *   mutationFn: async (id) => unwrap(await supabase.rpc('end_membership', { p_client: id }))
 */
export function unwrap<T>(response: { data: T; error: unknown }): T {
  if (response.error) throw response.error;
  return response.data;
}

/**
 * Like `unwrap`, for a row that must exist. RLS hides rows silently, so "no row" is
 * reported as Not found — never as "you may not have access".
 *
 *   queryFn: async () => unwrapRow(await supabase.from('gyms').select().maybeSingle())
 */
export function unwrapRow<T>(response: { data: T; error: unknown }): NonNullable<T> {
  if (response.error) throw response.error;
  if (response.data === null || response.data === undefined) {
    throw new GymError(MESSAGES.notFound, { code: 'P0002', status: 404 });
  }
  return response.data as NonNullable<T>;
}
